import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { CloudFunctions, getFunctionsUrl, getLanguageName } from '../../utils/utils';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { LanguageCodeInput } from '../LanguageCodeInput/LanguageCodeInput';
import styles from './SubtitleUploader.module.scss';

const SubtitleUploaderComponent = ({ show, onHide, onConfirm }) => {
  const { currentUser } = useFirebase();
  const [data, setData] = useState<{ languageCode: string; offset: number; file: File; filename: string }>({
    languageCode: '',
    offset: 0,
    file: null,
    filename: '',
  });
  const [isLanguageCodeValid, setIsLanguageCodeValid] = useState(false);
  const [languageName, setLanguageName] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (show) return;
    setIsLanguageCodeValid(false);
    setIsProcessing(false);
    setData({ languageCode: '', offset: 0, file: null, filename: '' });
  }, [show]);

  useEffect(() => {
    const languageName = getLanguageName(data.languageCode);
    setLanguageName(languageName);
    setData((current) => ({ ...current, filename: `${current.languageCode}-${languageName}.vtt` }));
  }, [data.languageCode]);

  return (
    <Modal show={show} onHide={onHide} backdrop={'static'} keyboard={false}>
      <Modal.Header>
        <Modal.Title>Carica sottotitoli</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LanguageCodeInput
          value={data.languageCode}
          onChange={(e) => setData((current) => ({ ...current, languageCode: e }))}
          disabled={isProcessing}
          onErrorChange={(e) => setIsLanguageCodeValid(!e)}
          autoFocus={true}
        />

        <Form.Group>
          <Form.Label>
            Shift <small className={'text-muted'}>(secondi)</small>
          </Form.Label>
          <Form.Control
            disabled={isProcessing}
            required
            type={'number'}
            value={data.offset}
            onChange={(e) => setData((current) => ({ ...current, offset: +e.target.value || 0 }))}
          />
          <Form.Text>Specificare uno shift in secondi. Lasciare a 0 (zero) se non necessario.</Form.Text>
          <Form.Text className={'mt-0'}>Un numero positivo ritarda i sottotitoli, un numero negativo li anticipa.</Form.Text>
        </Form.Group>

        <Form.Group>
          <Form.Label>
            File di sottotitoli * <small className={'text-muted'}>(VTT)</small>
          </Form.Label>
          <Form.File disabled={isProcessing} accept={'text/vtt'} onChange={(e) => setData((current) => ({ ...current, file: e.target.files[0] }))} />
        </Form.Group>

        {!!data.languageCode && isLanguageCodeValid && !!data.file && data.offset != null && !isProcessing && !!languageName && (
          <Alert variant={'primary'} className={'mb-0'}>
            I sottotitoli selezionati verranno caricati con il nome <strong className={styles.nowrap}>{data.filename}</strong> e verrà applicato uno
            shift di {data.offset} secondi.
          </Alert>
        )}
        {!!isProcessing && (
          <Alert variant={'warning'} className={'mb-0'}>
            Gestione del file in corso, attendere&hellip;
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant={'secondary'} onClick={onHide} disabled={isProcessing}>
          Annulla
        </Button>
        <Button
          variant={'primary'}
          onClick={async () => {
            setIsProcessing(true);
            const reader = new FileReader();
            reader.addEventListener('load', async (e) => {
              const result = (e.target as any).result as string;

              const response = await fetch(getFunctionsUrl(CloudFunctions.VttHandler), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  vtt: result,
                  offset: data.offset,
                  uid: currentUser.uid,
                  token: await currentUser.getIdToken(true),
                }),
              });
              const syncedVtt = await response.text();
              const syncedFile = new File([syncedVtt], data.filename, { type: 'text/vtt' });
              onConfirm({ languageCode: data.languageCode, file: syncedFile });
              onHide();
            });
            reader.readAsText(data.file);
          }}
          disabled={!data.languageCode || !isLanguageCodeValid || !data.file || data.offset == null || isProcessing || !languageName}
        >
          Conferma
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { SubtitleUploaderComponent as SubtitleUploader };
